/**
 * Site Refresh 2026: header behaviour (refresh-header / refresh-mega-menu /
 * refresh-phone-menu snippets). Self-contained, no build step; the theme's
 * own header script still handles search, cart count and sticky hiding.
 */
(() => {
	const header = document.querySelector("[data-rn-header]");

	if (!header) {
		return;
	}

	const section = header.closest(".header-section") || header.parentElement;

	/* Sticky bar: a slightly stronger shadow once the page has scrolled. */
	let scrollTicking = false;

	function updateScrolled() {
		section.classList.toggle("rn-scrolled", window.scrollY > 4);
		scrollTicking = false;
	}

	window.addEventListener(
		"scroll",
		() => {
			if (!scrollTicking) {
				scrollTicking = true;
				requestAnimationFrame(updateScrolled);
			}
		},
		{ passive: true }
	);

	updateScrolled();

	const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
	const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

	function focusables(root) {
		return [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
	}

	/* Menu photos are lazy so they cost nothing on page load; start fetching
	   them as soon as someone reaches for a menu, so panels open with photos. */
	function warmImages(root) {
		root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
			img.loading = "eager";
		});
	}

	function afterFade(callback) {
		if (reducedMotion.matches) {
			callback();
		} else {
			window.setTimeout(callback, 180);
		}
	}

	/* -----------------------------------------------------------------------
	   Desktop mega menus: hover (with a short delay), click or Enter to open;
	   Esc closes and returns focus to the top link. A second click on an open
	   top link follows it to its collection.
	   ----------------------------------------------------------------------- */
	const bar = header.querySelector("[data-rn-bar]");
	const nav = header.querySelector(".rn-nav");
	const mega = header.querySelector("[data-rn-mega]");
	const scrim = header.querySelector("[data-rn-scrim]");
	const tops = [...header.querySelectorAll("[data-rn-top]")];

	if (nav && mega && tops.length) {
		const OPEN_DELAY = 120;
		const SWITCH_DELAY = 60;
		const CLOSE_DELAY = 220;
		const menus = [...mega.querySelectorAll("[data-rn-menu]")];
		let current = null;
		let openTimer = 0;
		let closeTimer = 0;

		const isDesktop = () => window.getComputedStyle(nav).display !== "none";

		nav.addEventListener("pointerenter", () => warmImages(mega), { once: true });
		nav.addEventListener("focusin", () => warmImages(mega), { once: true });
		const topFor = (handle) => tops.find((top) => top.dataset.rnTop === handle);
		const menuFor = (handle) => menus.find((menu) => menu.dataset.rnMenu === handle);

		const clearTimers = () => {
			window.clearTimeout(openTimer);
			window.clearTimeout(closeTimer);
		};

		const placePanel = () => {
			const bottom = Math.round(bar.getBoundingClientRect().bottom);
			mega.style.setProperty("--rn-mega-top", `${bottom + 8}px`);
		};

		const openMenu = (handle, focusFirst) => {
			clearTimers();
			const menu = menuFor(handle);

			if (!menu) {
				return;
			}

			if (current && current !== handle) {
				topFor(current).setAttribute("aria-expanded", "false");
			}

			placePanel();
			menus.forEach((item) => {
				item.hidden = item !== menu;
			});
			topFor(handle).setAttribute("aria-expanded", "true");

			if (mega.hidden || !current) {
				mega.hidden = false;
				scrim.hidden = false;
				requestAnimationFrame(() => {
					mega.classList.add("is-open");
					scrim.classList.add("is-open");
				});
			}

			current = handle;

			if (focusFirst) {
				const first = focusables(menu)[0];
				first && first.focus();
			}
		};

		const closeMenu = (returnFocus) => {
			clearTimers();

			if (!current) {
				return;
			}

			const top = topFor(current);
			top.setAttribute("aria-expanded", "false");
			mega.classList.remove("is-open");
			scrim.classList.remove("is-open");
			current = null;

			afterFade(() => {
				if (current) {
					return;
				}
				mega.hidden = true;
				scrim.hidden = true;
			});

			if (returnFocus) {
				top.focus();
			}
		};

		const scheduleClose = () => {
			window.clearTimeout(openTimer);
			window.clearTimeout(closeTimer);
			closeTimer = window.setTimeout(() => closeMenu(false), CLOSE_DELAY);
		};

		/* The element Tab should reach after the last item of an open menu. */
		const nextAfter = (handle) => {
			const index = tops.findIndex((top) => top.dataset.rnTop === handle);
			const navItems = focusables(nav);
			const after = navItems[navItems.indexOf(tops[index]) + 1];

			if (after) {
				return after;
			}

			const rest = focusables(header).filter((el) => !nav.contains(el) && !mega.contains(el));
			return rest.find((el) => el.compareDocumentPosition(nav) & Node.DOCUMENT_POSITION_PRECEDING);
		};

		tops.forEach((top) => {
			const handle = top.dataset.rnTop;

			top.addEventListener("pointerenter", (event) => {
				if (event.pointerType === "touch" || !isDesktop()) {
					return;
				}
				window.clearTimeout(closeTimer);
				window.clearTimeout(openTimer);
				if (current === handle) {
					return;
				}
				openTimer = window.setTimeout(() => openMenu(handle, false), current ? SWITCH_DELAY : OPEN_DELAY);
			});

			top.addEventListener("pointerleave", (event) => {
				if (event.pointerType === "touch") {
					return;
				}
				scheduleClose();
			});

			top.addEventListener("click", (event) => {
				if (!isDesktop() || current === handle) {
					return;
				}
				event.preventDefault();
				openMenu(handle, false);
			});

			top.addEventListener("keydown", (event) => {
				if (event.key === "ArrowDown") {
					event.preventDefault();
					openMenu(handle, true);
				} else if (event.key === "Tab" && !event.shiftKey && current === handle) {
					const first = focusables(menuFor(handle))[0];
					if (first) {
						event.preventDefault();
						first.focus();
					}
				}
			});
		});

		mega.addEventListener("pointerenter", () => window.clearTimeout(closeTimer));
		mega.addEventListener("pointerleave", (event) => {
			if (event.pointerType !== "touch") {
				scheduleClose();
			}
		});

		mega.addEventListener("keydown", (event) => {
			if (event.key !== "Tab" || !current) {
				return;
			}
			const items = focusables(mega);
			const handle = current;

			if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
				const next = nextAfter(handle);
				if (next) {
					event.preventDefault();
					closeMenu(false);
					next.focus();
				}
			} else if (event.shiftKey && document.activeElement === items[0]) {
				event.preventDefault();
				topFor(handle).focus();
			}
		});

		scrim.addEventListener("click", () => closeMenu(false));

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && current) {
				closeMenu(true);
			}
		});

		document.addEventListener("click", (event) => {
			if (current && !nav.contains(event.target) && !mega.contains(event.target)) {
				closeMenu(false);
			}
		});

		header.addEventListener("focusout", (event) => {
			if (!current) {
				return;
			}
			const to = event.relatedTarget;
			if (to && !nav.contains(to) && !mega.contains(to)) {
				closeMenu(false);
			}
		});

		/* The theme hides the sticky header on scroll down; take the menu with it. */
		window.addEventListener(
			"scroll",
			() => {
				if (current && section.classList.contains("is-collapsed")) {
					closeMenu(false);
				}
			},
			{ passive: true }
		);

		window.addEventListener("resize", () => {
			if (current && !isDesktop()) {
				closeMenu(false);
			} else if (current) {
				placePanel();
			}
		});
	}

	/* -----------------------------------------------------------------------
	   Phone menu: a modal sheet. Traps focus, closes on Esc, the close button
	   or the scrim, and returns focus to the menu button. One accordion open
	   at a time.
	   ----------------------------------------------------------------------- */
	const drawer = section.querySelector("[data-rn-drawer]");
	const openers = [...header.querySelectorAll("[data-rn-drawer-open]")];

	if (drawer && openers.length) {
		const sheet = drawer.querySelector("[data-rn-drawer-sheet]");
		const toggles = [...drawer.querySelectorAll("[data-rn-acc]")];
		let opener = null;

		const isOpen = () => !drawer.hidden;

		const openDrawer = (button) => {
			opener = button;
			warmImages(drawer);
			drawer.hidden = false;
			document.body.style.overflow = "hidden";
			openers.forEach((item) => item.setAttribute("aria-expanded", "true"));
			requestAnimationFrame(() => drawer.classList.add("is-open"));
			const close = drawer.querySelector("[data-rn-drawer-close].rn-drawer__close");
			(close || sheet).focus();
		};

		const closeDrawer = () => {
			if (!isOpen()) {
				return;
			}
			drawer.classList.remove("is-open");
			openers.forEach((item) => item.setAttribute("aria-expanded", "false"));
			document.body.style.overflow = "";
			afterFade(() => {
				if (!drawer.classList.contains("is-open")) {
					drawer.hidden = true;
				}
			});
			if (opener) {
				opener.focus();
			}
		};

		openers.forEach((button) => {
			button.addEventListener("click", () => (isOpen() ? closeDrawer() : openDrawer(button)));
		});

		drawer.querySelectorAll("[data-rn-drawer-close]").forEach((button) => {
			button.addEventListener("click", closeDrawer);
		});

		drawer.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				closeDrawer();
				return;
			}
			if (event.key !== "Tab") {
				return;
			}
			const items = focusables(sheet);
			const first = items[0];
			const last = items[items.length - 1];

			if (event.shiftKey && (document.activeElement === first || document.activeElement === sheet)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		});

		/* Keep focus inside while the sheet is open. */
		document.addEventListener("focusin", (event) => {
			if (isOpen() && drawer.classList.contains("is-open") && !sheet.contains(event.target)) {
				const first = focusables(sheet)[0];
				(first || sheet).focus();
			}
		});

		toggles.forEach((toggle) => {
			toggle.addEventListener("click", () => {
				const expand = toggle.getAttribute("aria-expanded") !== "true";

				toggles.forEach((other) => {
					const on = other === toggle && expand;
					other.setAttribute("aria-expanded", on ? "true" : "false");
					const panel = document.getElementById(other.getAttribute("aria-controls"));
					if (panel) {
						panel.hidden = !on;
					}
				});
			});
		});

		/* The menu button disappears at desktop width; close the sheet with it. */
		window.addEventListener("resize", () => {
			if (isOpen() && openers.every((button) => button.getClientRects().length === 0)) {
				closeDrawer();
			}
		});
	}
})();
